import { Box, Button, Typography } from "@mui/material";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import TableRowsOutlined from "@mui/icons-material/TableRowsOutlined";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import React from "react";
import { tokens } from "../../../theme";
import { tints } from "../../../theme/overrides";

export type EmptyStateKind = "empty" | "filtered" | "error";

interface EmptyStateProps {
  kind: EmptyStateKind;
  title: string;
  body: string;
  action?: { label: string; onClick: () => void };
  /** Rendered under the message for the filtered case, so the cause is visible. */
  filters?: React.ReactNode;
}

const styles: Record<EmptyStateKind, any> = {
  empty: {
    border: `1px solid ${tokens.color.border}`,
    bg: tints.neutralPanel,
    iconBg: tints.neutralChip,
    iconFg: tokens.color.faint,
    titleColor: tokens.color.ink,
    bodyColor: tokens.color.muted,
    Icon: TableRowsOutlined,
    variant: "outlined" as const
  },
  filtered: {
    border: `1px solid ${tokens.color.borderHover}`,
    bg: tints.primaryPanel,
    iconBg: tints.primaryChip,
    iconFg: tokens.color.primary,
    titleColor: tokens.color.ink,
    bodyColor: tokens.color.body,
    Icon: FilterAltOutlined,
    variant: "contained" as const
  },
  error: {
    border: `1px solid ${tints.cautionBorder}`,
    bg: tints.cautionPanel,
    iconBg: tints.cautionChip,
    iconFg: tokens.color.caution.main,
    titleColor: tokens.color.caution.text,
    bodyColor: tokens.color.caution.text,
    Icon: WarningAmberOutlined,
    variant: "outlined" as const
  }
};

/**
 * Whole-surface empty state for tables, charts and panes.
 *
 * The "filtered" kind is the important one: today it is indistinguishable from
 * having no data, so users conclude the account is empty rather than that they
 * over-filtered. It must always name the filters and offer a way out.
 */
const EmptyState = ({ kind, title, body, action, filters }: EmptyStateProps) => {
  const s = styles[kind];
  const { Icon } = s;

  return (
    <Box
      sx={{
        border: s.border,
        bgcolor: s.bg,
        borderRadius: `${tokens.radius.card}px`,
        p: "34px 22px",
        minHeight: 196,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        gap: 1.4
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: "11px",
          bgcolor: s.iconBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <Icon sx={{ fontSize: 21, color: s.iconFg }} />
      </Box>
      <Typography sx={{ fontSize: 14.5, fontWeight: 600, color: s.titleColor }}>{title}</Typography>
      <Typography sx={{ fontSize: 12.5, color: s.bodyColor, lineHeight: 1.55, maxWidth: 280, textWrap: "pretty" }}>
        {body}
      </Typography>
      {filters}
      {action && (
        <Button variant={s.variant} size="small" onClick={action.onClick} sx={{ mt: 0.5 }}>
          {action.label}
        </Button>
      )}
    </Box>
  );
};

export default EmptyState;
